import { useTranslation } from 'react-i18next';

import InformationLine from '@/assets/icons/information_line.svg?component';
import { Card, CardHeader } from '@/components/atoms/Card';
import FishHealthSummary from '@/components/atoms/FishHealthSummary/FishHealthSummary';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { WelfareData } from '@/domain/welfare/types';
import chartBlockStyles from '@/routes/Client/Cage/pages/Health/components/HealthDetailChartContentBlock.module.scss';
import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '@/routes/Client/Cage/pages/Health/components/healthDetailChartConstants';

import { useScaleLossChart } from './useScaleLossChart';

import styles from './CageWelfareScaleloss.module.scss';

const CHART_HEIGHT = 360;

export interface CageWelfareScalelossPageContentProps {
    fishHealthData: WelfareData;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (data: DateRange) => void;
}

const DEFAULT_EQ_THRESHOLD = 0.02 / 7;

export function CageWelfareScalelossPageContent(props: CageWelfareScalelossPageContentProps) {
    const { fishHealthData, dateFrom, dateTo, onDateChange } = props;
    const { t } = useTranslation();

    const { lastEstimate, slopePerDay } = fishHealthData;
    const chart = useScaleLossChart(fishHealthData);

    const getStatus = (value: number) => {
        if (value > DEFAULT_EQ_THRESHOLD) {
            return 'negative';
        }
        if (value < -DEFAULT_EQ_THRESHOLD) {
            return 'positive';
        }
        return 'neutral';
    };

    const getDirectionOld = (value: number) =>
        value > DEFAULT_EQ_THRESHOLD ? 'up' : value < -DEFAULT_EQ_THRESHOLD ? 'down' : 'flat';

    const summary = {
        skinSpeckles: {
            value: lastEstimate.skinSpeckles,
            slope: slopePerDay.skinSpeckles,
            status: getStatus(slopePerDay.skinSpeckles ?? 0),
            direction: getDirectionOld(slopePerDay.skinSpeckles ?? 0),
        },
    } as const;

    return (
        <div className={styles.pageBoxLayout}>
            <div className={styles.leftSideBox}>
                <Card padding="lg">
                    <FishHealthSummary
                        dataRate={summary.skinSpeckles.value}
                        status={summary.skinSpeckles.status}
                        variant="inline"
                        direction={summary.skinSpeckles.direction}
                        label="skinSpeckles"
                    />
                </Card>
                <ContentBlock
                    title={t('fishHealthIndicators.skinSpeckles')}
                    size="compact"
                    spacing="none"
                    className={chartBlockStyles.chartBlock}
                    containerRef={chart.fullScreenContainerRef}
                    headerActions={
                        <ChartHeaderActions
                            chartRef={chart.chartRef}
                            chartName="welfare-scale-loss-chart"
                            isFullScreen={chart.isFullScreen}
                            menuDisabled={fishHealthData.days.length === 0}
                            onFullScreenToggle={chart.handleFullScreenToggle}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            onDateChange={onDateChange}
                            defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                        />
                    }>
                    <div className={chartBlockStyles.chartBlockBody}>
                        <MetricChart
                            kind="line"
                            chartRef={chart.chartRef}
                            data={chart.data}
                            options={chart.options}
                            height={CHART_HEIGHT}
                            legendItems={chart.legendItems}
                            visibleSeries={chart.visibleSeries}
                            onToggleSeries={chart.toggleSeries}
                        />
                    </div>
                </ContentBlock>
            </div>
            <Card
                padding="lg"
                header={
                    <CardHeader
                        title={t('cageWelfare.scaleLoss.scaleLoss')}
                        level="h2"
                        size="card"
                        icon={<TitleIcon icon={InformationLine} tone="info" />}
                    />
                }
            />
        </div>
    );
}
